/**
 * Master Sheet — Allie's workbook, rebuilt from the portal's own ledgers.
 *
 * Its own page under Production as of 2026-08-28 (Max), above Product Line. It was a tab inside the S&P
 * Hub, which is where it was built, but it is not a reconciliation screen: the Hub's tabs answer "do the
 * ledgers agree", and this one is the sheet the production team works in every day. Moving it also gives
 * it the whole width, which a 32-column table needs.
 *
 * The data comes from dbo.[00_PORTAL_sp_master_sheet_view] (sql/20260827c_master_sheet_view.sql) and
 * everything a person types lands in the two tables from sql/20260828_master_sheet_entry.sql. The API is
 * still under /api/sp — same permission key, same backend section — because the view reads the S&P
 * ledgers and splitting the route would not make it a different thing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Database, RefreshCw, FileDown, Plus, Pencil, Trash2, Columns3, Split, X, Globe, ListChecks, Eye } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import SPLedgerHeader from './SPLedgerHeader';
import useAuth from './useAuth';
import { DestinationStep } from './36_ShipDocSteps';
import MasterSheetChanges from './MasterSheetChanges';
import { BACKEND_URL } from './backendConfig';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';

/* THE GUIDE (Max 01/11: every module has one). The row buttons are the first row's: a PLANNED row has no Edit, so the
   step speaks of the button column as a whole. */
const TOUR = [
  { selector: '.sp-head-btns', title: 'tourMasterSheet.btnsT', body: 'tourMasterSheet.btnsB' },
  { selector: '.sp-legend', title: 'tourMasterSheet.legendT', body: 'tourMasterSheet.legendB' },
  { selector: '.sp-toolbar', title: 'tourMasterSheet.toolbarT', body: 'tourMasterSheet.toolbarB' },
  { selector: '.sp-scrollmaster', title: 'tourMasterSheet.sheetT', body: 'tourMasterSheet.sheetB' },
  { selector: '.sp-scrollmaster tbody td.sp-actioncell', title: 'tourMasterSheet.rowT', body: 'tourMasterSheet.rowB' },
];

const API_BASE = `${BACKEND_URL}/api/sp`;

/*
 * The form. Everything a person can type is typed HERE (Max, 2026-08-28) — there is no editing inside the
 * table any more. One form for both jobs: adding a row that exists in no ledger, and editing one that does.
 *
 * On a LEDGER row only the human-owned fields are inputs; the rest are shown read-only, because a row is
 * easier to recognise by its SAP code and description than by its key. On a MANUAL row everything is an
 * input — there is no ledger behind it to supply anything.
 *
 * Nothing is required (Max): filling in any one field is enough to save.
 */
const MASTER_FORM_FIELDS = [
  { key: 'Factory' }, { key: 'Season' }, { key: 'Vendor_PO' }, { key: 'Cycle_No' },
  { key: 'HNX_PO' }, { key: 'Customer_PO' }, { key: 'Shipping_Mark_PO' }, { key: 'SAP_Code' },
  { key: 'SFP_Code' }, { key: 'Item_No' }, { key: 'Barcode' }, { key: 'MB_Barcode' },
  { key: 'MB_Item_Code' }, { key: 'AF_Code' }, { key: 'Description' }, { key: 'Color' },
  { key: 'Frame' }, { key: 'Destination' },
  { key: 'Quantity', type: 'number' }, { key: 'Unit_Price', type: 'number' },
  { key: 'PO_Issued_Date', type: 'date' }, { key: 'Original_Delivery_Date', type: 'date' },
  { key: 'TLO_Status' }, { key: 'Status' }, { key: 'Cycle_Time' }, { key: 'TTL_Cycle_Time' },
  { key: 'Remark' }, { key: 'Classification' },
];

/* Which columns are on screen. Same shape as the Project Tracker's picker (PT_PROJECT_COL_STORE) so
   the two behave identically: a map of key -> false for the hidden ones, kept in localStorage, and an
   absent key means visible. Storing the HIDDEN ones rather than the visible ones is what makes a new
   column — Classification today, whatever comes next — appear for everybody instead of staying
   invisible until they press Show all. */
const MASTER_COL_STORE = 'hevn_master_sheet_columns_v1';
// Factory is the row's anchor and stays. Unchecking every box would otherwise leave a table of row
// numbers and Edit buttons, which looks broken rather than empty.
const MASTER_COL_LOCKED = new Set(['Factory']);
const loadMasterColumnVisible = () => {
  try { return JSON.parse(localStorage.getItem(MASTER_COL_STORE) || '{}') || {}; } catch (e) { return {}; }
};
// A date input wants yyyy-mm-dd; the API returns an ISO timestamp.
const masterDateValue = (v) => (v ? String(v).slice(0, 10) : '');

const MASTER_MONEY = new Set(['Price_Without_Frame', 'Unit_Price', 'Total_Without_Frame', 'Total_At_Vendor']);
// Year is NOT in here: 2027 came out as "2,027". Month and Year are labels that happen to be numbers.
const MASTER_NUM = new Set(['Quantity']);
const MASTER_PLAIN_NUM = new Set(['Month', 'Year']);
const fmtMasterCell = (column, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (MASTER_MONEY.has(column)) {
    return Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (MASTER_NUM.has(column)) return Number(value).toLocaleString('en-GB');
  if (MASTER_PLAIN_NUM.has(column)) return String(value);
  if (column === 'PO_Issued_Date' || column === 'Original_Delivery_Date') return String(value).slice(0, 10);
  return String(value);
};

export default function MasterSheet() {
  const { t } = useTranslation();
  const [master, setMaster] = useState(null);
  const [masterColumnVisible, setMasterColumnVisible] = useState(loadMasterColumnVisible);
  const [masterColumnMenuOpen, setMasterColumnMenuOpen] = useState(false);
  /* The split editor. Held as one object rather than five useStates because it is opened, filled and
     closed as a unit, and a half-open editor with stale children from the last row is the obvious
     failure mode. */
  const [masterSplit, setMasterSplit] = useState(null);
  /* The row waiting on a delete confirmation. The browser's own confirm() was used here until
     2026-08-28; Max asked for the portal's dialog instead, the same call he made on the pricing
     calculator — a native alert looks like the page has gone wrong. */
  const [masterConfirmDel, setMasterConfirmDel] = useState(null);
  /* The next running Vendor PO for the factory picked in the insert form. */
  const [masterNextVendorPo, setMasterNextVendorPo] = useState(null);
  const [masterRows, setMasterRows] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterExporting, setMasterExporting] = useState(false);
  // The "what changed since I last looked" dialog (2026-10-09, Max for Tram) — see MasterSheetChanges.jsx.
  const [changesOpen, setChangesOpen] = useState(false);
  const [masterError, setMasterError] = useState('');
  const [masterSaving, setMasterSaving] = useState(false);
  /*
   * The open form, or null. `{ mode: 'new' | 'edit', row, values }`.
   *
   * A panel above the table rather than inputs inside it: the list is sorted by delivery date, so a row
   * being typed at the top would jump elsewhere the moment it was saved, and 32 columns of live inputs is
   * not something anyone can read.
   */
  const [masterForm, setMasterForm] = useState(null);
  const [masterSyncing, setMasterSyncing] = useState('');
  // Distinct values per column, fetched once when the form first opens, so Factory is a pick from the six
  // real plants and Season / Cycle No offer what already exists instead of inviting a fourth spelling of
  // "Buy#1 SLT". Datalists, not hard selects (except Factory): a value that does not exist yet must still
  // be typeable — that is the whole point of a human-owned column.
  const [masterOptions, setMasterOptions] = useState(null);
  const masterPage = useRef(1);
  const masterBoxRef = useRef(null);
  const masterBusy = useRef(false);
  const masterSeq = useRef(0);
  const MASTER_PAGE = 50;
  const [masterQuery, setMasterQuery] = useState({
    source: '', search: '', sort: '', dir: 'desc', filters: {},
  });

  /**
   * 26,223 rows, so this pages on scroll the way Hub View does rather than loading the lot: 32 columns
   * across 26,000 rows is a DOM the browser would feel. The in-flight guard is load-bearing — a scroll
   * event fires many times on the way down and without it the same page would be appended twice.
   */
  const loadMaster = useCallback((query, { append = false } = {}) => {
    /* AN APPEND IS DROPPED WHILE SOMETHING IS IN FLIGHT; A FRESH QUERY SUPERSEDES IT. The guard used
       to cover both, and that swallowed searches: the person types a PO, presses Enter, and the call
       is thrown away because the first page is still loading — the sheet goes on showing every row
       with their search sitting in the box. Measured 2026-10-02, the first load costs ~4 seconds,
       which is long enough to type into. A scroll still needs the guard for its own reason: the event
       fires many times on the way down and the same page would be appended twice. */
    if (append && masterBusy.current) return;
    const page = append ? masterPage.current + 1 : 1;
    // Which request this is. Two can now be open at once, and the older one's answer describes a
    // query nobody is looking at any more.
    const seq = (masterSeq.current += 1);
    masterBusy.current = true;
    setMasterLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ page: String(page), pageSize: String(MASTER_PAGE) });
        if (query.source) params.set('source', query.source);
        if (query.search) params.set('search', query.search);
        if (query.sort) { params.set('sort', query.sort); params.set('dir', query.dir || 'desc'); }
        if (query.filters && Object.keys(query.filters).length) {
          params.set('filters', JSON.stringify(query.filters));
        }
        const response = await fetch(`${API_BASE}/master-sheet?${params}`, { credentials: 'include' });
        const data = await response.json();
        if (seq !== masterSeq.current) return;
        if (response.ok) {
          masterPage.current = page;
          setMaster(data);
          setMasterRows((prev) => (append ? [...prev, ...(data.rows || [])] : (data.rows || [])));
        } else setMasterError(data.error || i18n.t('xMaster.readFailed', { status: response.status }));
      } catch (err) {
        // leave what is on screen rather than blanking the table
      } finally {
        // Only the latest request may say the sheet has stopped loading; an overtaken one finishing
        // would otherwise clear the flag while its replacement is still out.
        if (seq === masterSeq.current) {
          masterBusy.current = false;
          setMasterLoading(false);
        }
      }
    })();
  }, []);

  // Any change to the query restarts the list: rows come back in a new order, and keeping the pages already
  // scrolled in would mix two orderings.
  useEffect(() => {
    masterPage.current = 1;
    setMasterRows([]);
    loadMaster(masterQuery, { append: false });
  }, [masterQuery, loadMaster]);

  const onMasterScroll = (event) => {
    const el = event.currentTarget;
    if (masterLoading || !master) return;
    if (masterRows.length >= master.total) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 120) return;
    loadMaster(masterQuery, { append: true });
  };

  // If the rows loaded do not fill the box no scroll can ever fire and the list stops dead; top up until
  // it can scroll.
  useEffect(() => {
    if (masterLoading || !master) return;
    const el = masterBoxRef.current;
    if (!el || masterRows.length >= master.total) return;
    if (el.scrollHeight > el.clientHeight + 8) return;
    loadMaster(masterQuery, { append: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLoading, master, masterRows.length]);

  /* A PATCH THAT CHANGES NOTHING CHANGES NOTHING. The query object is the effect's dependency, so a
     fresh object with identical values restarted the list: clicking out of the search box — which is
     what opening the row form does — re-ran the whole sheet query for no reason. That reload is also
     how a saved row came to be un-saved on screen, below. */
  const setMasterFilter = (patch) => setMasterQuery((prev) => {
    const next = { ...prev, ...patch };
    const same = Object.keys(next).every((k) => (k === 'filters'
      ? JSON.stringify(next[k]) === JSON.stringify(prev[k])
      : next[k] === prev[k]));
    return same ? prev : next;
  });

  /**
   * Export what is on screen. The filters and the sort go with the request, so the file cannot disagree
   * with the table it came from — and the SERVER walks the whole result set, not the pages scrolled in, so
   * a 26,000-row export does not require scrolling to the bottom first.
   */
  const exportMaster = async () => {
    setMasterExporting(true);
    setMasterError('');
    try {
      const response = await fetch(`${API_BASE}/master-sheet/export-xlsx`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // The picker drives the file too, the way it does on the Project Tracker export.
        body: JSON.stringify({ ...masterQuery, columns: visibleMasterColumns }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('xMaster.exportFailed', { status: response.status }));
      }
      const blob = await response.blob();
      const name = (response.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1]
        || 'Master_sheet.xlsx';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMasterError(err.message);
    } finally {
      setMasterExporting(false);
    }
  };

  /* GUARDED BY A REF, NOT BY `masterOptions`. Two callers ask for this set — the page on mount and
     the form when it opens — and the state they both test is still null until the seventh request
     has answered, so both fired the lot: 14 requests measured, each one a distinct-values scan of
     the sheet. The save queues behind them on the same six connections, which is why pressing Save
     with the form freshly opened sat on "Saving…". */
  const masterOptionsBusy = useRef(false);
  const loadMasterOptions = useCallback(async () => {
    if (masterOptionsBusy.current) return;
    masterOptionsBusy.current = true;
    const OPTION_COLUMNS = ['Factory', 'Season', 'Cycle_No', 'Color', 'Destination', 'TLO_Status', 'Status'];
    try {
      const results = await Promise.all(OPTION_COLUMNS.map(async (column) => {
        const response = await fetch(`${API_BASE}/master-values?column=${column}`, { credentials: 'include' });
        const data = await response.json().catch(() => ({}));
        return [column, (data.values || []).filter((v) => v !== data.blankLabel)];
      }));
      setMasterOptions(Object.fromEntries(results));
    } catch (err) {
      // no suggestions is not an error the user needs to see — the inputs still work as plain text
      setMasterOptions({});
    } finally {
      masterOptionsBusy.current = false;
    }
  }, []);
  useEffect(() => {
    if (masterForm && !masterOptions) loadMasterOptions();
  }, [masterForm, masterOptions, loadMasterOptions]);

  /* When somebody picks a factory on a new row, fetch that factory's next running Vendor PO and
     offer it. Offered, not forced: it is filled in only while the field is still empty, so a number
     typed by hand is never overwritten by a background fetch landing a moment later. */
  const suggestVendorPo = useCallback(async (factory) => {
    setMasterNextVendorPo(null);
    if (!factory) return;
    try {
      const response = await fetch(
        `${API_BASE}/master-sheet/next-vendor-po?factory=${encodeURIComponent(factory)}`,
        { credentials: 'include' });
      const data = await response.json();
      if (!response.ok || !data.next) { setMasterNextVendorPo(data.note ? data : null); return; }
      setMasterNextVendorPo(data);
      setMasterForm((prev) => (prev && prev.mode === 'new' && !String(prev.values.Vendor_PO || '').trim()
        ? { ...prev, values: { ...prev.values, Vendor_PO: data.next } }
        : prev));
    } catch (error) {
      setMasterNextVendorPo(null);
    }
  }, []);

  /** Open the form on an existing row, with the values it already holds. */
  /* VIEW — Jay, 2026-10-16: "missing 'VIEW' button … Same for all similar function". The same form with
     nothing editable: every field greyed the way the ledger-owned ones already are, one Close. */
  const openMasterEdit = (row, view = false) => {
    const values = {};
    MASTER_FORM_FIELDS.forEach((f) => {
      const raw = row[f.key];
      values[f.key] = f.type === 'date' ? masterDateValue(raw) : (raw ?? '');
    });
    setMasterForm({ mode: view ? 'view' : 'edit', row, values });
    setMasterError('');
  };

  /* The columns actually rendered. Everything downstream — the header, the body, the sticky run and
     the export payload — reads this rather than master.columns, so hiding a column cannot leave one
     of them out of step with the others. */
  const visibleMasterColumns = (master?.columns || [])
    .filter((c) => MASTER_COL_LOCKED.has(c) || masterColumnVisible[c] !== false);
  const setMasterColumns = (next) => {
    setMasterColumnVisible(next);
    try { localStorage.setItem(MASTER_COL_STORE, JSON.stringify(next)); } catch (e) { /* private mode */ }
  };
  const hiddenMasterCount = (master?.columns || []).length - visibleMasterColumns.length;
  /* The destination mapping, opened from the toolbar rather than given a tab of its own: it is
     something somebody settles once and then leaves alone, and the sheet is what they came for.
     `permissions` carries the LEVEL and `functions` only the keys, so the level is read here for
     the same reason SAP Ship Doc reads it: drawing live boxes for a view-only holder would have the
     server 403 every save. */
  const [destOpen, setDestOpen] = useState(false);
  const { permissions } = useAuth();
  const mayEditMaster = (permissions || {}).sp_master_sheet === 'edit';
  const toggleMasterColumn = (key, visible) => setMasterColumns({ ...masterColumnVisible, [key]: visible });
  const showAllMasterColumns = () => setMasterColumns({});

  /* --------------------------------------------------------------------------------------------
     Splitting a PO line into the destinations it is for.

     Typed end to end, and that is not a UI preference — it was measured. SAP holds no link from an
     SFP purchase to a sales order (the requisitions are MRP-generated with no account assignment),
     deriving the split through the paired finished good produces an order of magnitude too many rows
     with quantities that reconcile on not one line, and a third of the customer POs the PT team uses
     are not in SAP at all.

     A candidate list from those sales orders was built first and removed on 2026-08-28 (Max): a row
     of plausible numbers beside an empty form invites somebody to click one BECAUSE it was offered,
     and the portal genuinely does not know which of them a stock replenishment purchase was for.
     Destination is a dropdown of the values already in use — that helps with spelling without
     suggesting an answer.
     -------------------------------------------------------------------------------------------- */
  const openMasterSplit = async (row) => {
    const parentKey = row.Split_Parent || row.Row_Key;
    // The Destination dropdown reads the same list the edit form does; load it if this is the first
    // thing opened in the session, otherwise the select is empty on the first split of the day.
    if (!masterOptions) loadMasterOptions();
    setMasterSplit({ parentKey, loading: true, children: [], error: '' });
    try {
      const response = await fetch(`${API_BASE}/master-sheet/split/${encodeURIComponent(parentKey)}`,
        { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xMaster.splitOpenFailed', { status: response.status }));
      setMasterSplit({
        parentKey,
        loading: false,
        parent: data.parent,
        error: '',
        children: (data.children || []).map((c) => ({
          customerPo: c.customer_po || '', destination: c.destination || '',
          qty: c.qty === null || c.qty === undefined ? '' : String(Number(c.qty)),
          isStock: !!c.is_stock, note: c.note || '',
        })),
      });
    } catch (error) {
      setMasterSplit({ parentKey, loading: false, children: [], error: error.message });
    }
  };

  const splitTotal = (masterSplit?.children || []).reduce(
    (sum, c) => sum + (String(c.qty).trim() === '' ? 0 : Number(c.qty) || 0), 0);
  const splitParentQty = Number(masterSplit?.parent?.quantity || 0);
  // Rounded to the ledger's own scale, so a value that IS equal does not read as off by 0.0000001.
  const splitRemainder = Math.round((splitParentQty - splitTotal) * 10000) / 10000;

  const setSplitChildren = (fn) => setMasterSplit((prev) => (
    prev ? { ...prev, children: fn(prev.children), error: '' } : prev));

  const saveMasterSplit = async () => {
    if (!masterSplit) return;
    setMasterSplit((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const response = await fetch(
        `${API_BASE}/master-sheet/split/${encodeURIComponent(masterSplit.parentKey)}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ children: masterSplit.children }),
        });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xMaster.saveFailed', { status: response.status }));
      setMasterSplit(null);
      // The row count changes — one parent becomes N children — so the page is reloaded rather than
      // patched in place; patching would leave the "x of y rows" counter lying.
      loadMaster(masterQuery, { append: false });
    } catch (error) {
      setMasterSplit((prev) => (prev ? { ...prev, saving: false, error: error.message } : prev));
    }
  };

  /** Which fields this form may actually write. A ledger row supplies its own system columns. */
  const masterFormEditable = (form) => {
    if (!form) return [];
    if (form.mode === 'new' || form.row?.Is_Manual) return MASTER_FORM_FIELDS.map((f) => f.key);
    const allowed = new Set(master?.editable || []);
    return MASTER_FORM_FIELDS.map((f) => f.key).filter((k) => allowed.has(k)
      // The ledger owns these two when it has them, so the input is not offered at all. Season came
      // off this list on 2026-08-28: no importer writes it, so there is nothing to protect it from.
      && !(k === 'Cycle_No' && form.row?.Cycle_No_Locked)
      // Customer PO is VBAK-BSTNK, and since 2026-10-20 a typed one WINS and survives the import (Max:
      // "MS" and "MS." for an order split across two POs) — the server never locks it now; the check
      // stays so an older server's answer is still honoured.
      && !(k === 'Customer_PO' && form.row?.Customer_PO_Locked));
  };

  const saveMasterForm = async () => {
    if (!masterForm) return;
    setMasterSaving(true);
    setMasterError('');
    try {
      const keys = masterFormEditable(masterForm);
      const values = {};
      keys.forEach((k) => { values[k] = masterForm.values[k]; });
      const isNew = masterForm.mode === 'new';
      const response = await fetch(`${API_BASE}/master-sheet/row`, {
        method: isNew ? 'POST' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isNew ? { values } : {
          rowKey: masterForm.row.Row_Key,
          hnxPo: masterForm.row.HNX_PO,
          factory: masterForm.row.Factory,
          values,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xMaster.saveFailed', { status: response.status }));
      /* RETIRE ANY LIST REQUEST THAT WAS ALREADY OUT. The rows are patched in place below, and a
         response to a request issued BEFORE this save describes the sheet without it — measured
         2026-10-02, a list asked for at 0.0s answered at 6.9s and wiped a save that landed at 4.3s,
         so the edit appeared and then vanished with nothing on screen to say why. Same sequence
         number the loader uses, so there is one rule about which answers still count. */
      masterSeq.current += 1;
      if (isNew) {
        // Straight to the top of what is on screen, so the row somebody just typed is where they are
        // already looking rather than wherever the sort would put it.
        if (data.row) setMasterRows((prev) => [data.row, ...prev]);
        setMaster((prev) => (prev ? { ...prev, total: (prev.total || 0) + 1 } : prev));
      } else {
        const changedVendorPo = keys.includes('Vendor_PO');
        setMasterRows((prev) => prev.map((r) => {
          if (r.Row_Key === masterForm.row.Row_Key) return { ...r, ...data.row };
          // Vendor PO belongs to the PO, so every line of it on screen moves together.
          if (changedVendorPo && r.HNX_PO && r.HNX_PO === masterForm.row.HNX_PO) {
            return { ...r, Vendor_PO: data.row?.Vendor_PO ?? null, Is_Edited: true };
          }
          return r;
        }));
      }
      setMasterForm(null);
    } catch (err) {
      setMasterError(err.message);
    } finally {
      setMasterSaving(false);
    }
  };

  /** Delete a manual row. Only offered on MAN-… rows; the API refuses anything else too. */
  const deleteMasterRow = async (row) => {
    if (!row?.Is_Manual) return;
    setMasterConfirmDel(null);
    setMasterSaving(true);
    setMasterError('');
    try {
      const response = await fetch(`${API_BASE}/master-sheet/row/${row.Row_Key}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xMaster.deleteFailed', { status: response.status }));
      setMasterRows((prev) => prev.filter((r) => r.Row_Key !== row.Row_Key));
      setMaster((prev) => (prev ? { ...prev, total: Math.max(0, (prev.total || 1) - 1) } : prev));
      setMasterForm((prev) => (prev?.row?.Row_Key === row.Row_Key ? null : prev));
    } catch (err) {
      setMasterError(err.message);
    } finally {
      setMasterSaving(false);
    }
  };

  /** The same manual refresh the Connections tab runs, reached from the screen that reads the result. */
  const syncMaster = async (kind) => {
    setMasterSyncing(kind);
    setMasterError('');
    try {
      const url = kind === 'ns'
        ? `${API_BASE}/netsuite/import`
        : `${API_BASE}/sap/import/po?apply=1`;
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('xMaster.syncFailed', { status: response.status }));
      if (kind === 'sap') {
        // SAP is two ledgers and the master sheet reads both. In SEQUENCE, never together: they share
        // the refresh lock, so firing them at once makes the second refuse the first.
        const so = await fetch(`${API_BASE}/sap/import/so?apply=1`, { method: 'POST', credentials: 'include' });
        const soData = await so.json().catch(() => ({}));
        if (!so.ok) throw new Error(soData.error || t('xMaster.soSyncFailed', { status: so.status }));
      }
      masterPage.current = 1;
      setMasterRows([]);
      loadMaster(masterQuery, { append: false });
    } catch (err) {
      setMasterError(err.message);
    } finally {
      setMasterSyncing('');
    }
  };

  return (
    <div className="sp-page">
      {/* The Style Hub's own shell, not the generic .content wrapper: .content has no horizontal padding of
          its own, which put this table hard against the sidebar while every other sp- page sits 22px off it
          (Max, 2026-08-28). .sp-main carries the header offset and the page padding in one place. */}
      <Header />
      <div className="sp-main">
        <div>
          <div className="mh-titlerow">
          <div className="sp-title">
            <h2>{t('xShell.masterSheet')}</h2>
            <p>{t('xMaster.subtitle')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="masterSheet" ready={!!master && !masterLoading} />
          </div>

        <section className="sp-section">
          <div className="sp-section-head">
            {/* No title here: the page above already says Master Sheet, and saying it twice on one
                screen reads as two different things. */}
            <div>
              <Database size={14} />{' '}
              {/* Counted, not hard-coded: the number moved the day Classification was added and a
                  stale "32" on the one screen whose subject IS the column list reads as a bug. */}
              {hiddenMasterCount > 0
                ? t('xMaster.columnsHidden', { n: (master?.columns || []).length, h: hiddenMasterCount })
                : t('xMaster.columnsCount', { n: (master?.columns || []).length })}
            </div>
            <div className="sp-head-btns">
              <button
                type="button"
                className="sp-btn"
                disabled={!!masterSyncing}
                onClick={() => syncMaster('sap')}
              >
                <RefreshCw size={14} /> {masterSyncing === 'sap' ? t('xMaster.syncingSap') : t('xMaster.syncSap')}
              </button>
              <button
                type="button"
                className="sp-btn"
                disabled={!!masterSyncing}
                onClick={() => syncMaster('ns')}
              >
                <RefreshCw size={14} /> {masterSyncing === 'ns' ? t('xMaster.syncingNs') : t('xMaster.syncNs')}
              </button>
              <button
                type="button"
                className="sp-btn"
                disabled={!!masterForm}
                onClick={() => setMasterForm({
                  mode: 'new',
                  row: null,
                  values: Object.fromEntries(MASTER_FORM_FIELDS.map((f) => [f.key, ''])),
                })}
              >
                <Plus size={14} /> {t('xMaster.addRow')}
              </button>
              <div className="sp-colmenu-wrap">
                <button
                  type="button"
                  className="sp-btn"
                  onClick={(event) => { event.stopPropagation(); setMasterColumnMenuOpen((open) => !open); }}
                >
                  <Columns3 size={14} /> {t('xMaster.columns')}
                  {hiddenMasterCount > 0 && <em>&nbsp;{t('xMaster.hiddenTag', { n: hiddenMasterCount })}</em>}
                </button>
                {masterColumnMenuOpen && (
                  <>
                    {/* Click-away, not a blur handler: a blur fires before the checkbox it was
                        heading for receives the click, which closed the menu on every tick. */}
                    <div className="sp-colmenu-back" onClick={() => setMasterColumnMenuOpen(false)} />
                    <div className="sp-colmenu">
                      <div className="sp-colmenu-h">
                        {t('xMaster.showColumns')}
                        <button type="button" onClick={showAllMasterColumns}>{t('xMaster.showAll')}</button>
                      </div>
                      {(master?.columns || []).map((column) => {
                        const isLocked = MASTER_COL_LOCKED.has(column);
                        return (
                          <label key={column} className={isLocked ? 'locked' : ''}>
                            <input
                              type="checkbox"
                              checked={isLocked || masterColumnVisible[column] !== false}
                              disabled={isLocked}
                              onChange={(event) => toggleMasterColumn(column, event.target.checked)}
                            />
                            <span className={`sp-colmenu-dot g-${master?.groups?.[column] || 'system'}`} />
                            <span>{master?.labels?.[column] || column.replace(/_/g, ' ')}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <button type="button" className="sp-btn" onClick={() => setDestOpen(true)}>
                <Globe size={14} /> {t('xMaster.destNames')}
              </button>
              <button type="button" className="sp-btn" onClick={() => setChangesOpen(true)}
                title={t('xMaster.checkChangesHint')}>
                <ListChecks size={14} /> {t('xMaster.checkChanges')}
              </button>
              <button type="button" className="sp-btn primary" disabled={masterExporting} onClick={exportMaster}>
                <FileDown size={14} /> {masterExporting ? t('xMaster.building') : t('xMaster.exportExcel')}
              </button>
            </div>
          </div>

          <p className="sp-note">
            <Trans t={t} i18nKey="xMaster.intro" components={{ b: <strong /> }} />
          </p>
          {(() => {
            /* Which columns are somebody's job. Max asked for this on 2026-08-28: with 32 columns and a
               lot of blanks, there was no way to tell an empty cell that is waiting for a person from an
               empty cell the importer simply has not filled yet. The colour is on the whole column, header
               and body, because that is the direction the eye actually travels when scanning a sheet. */
            const groups = master?.groups || {};
            const named = (kind) => (master?.columns || [])
              .filter((c) => groups[c] === kind)
              .map((c) => master?.labels?.[c] || c);
            const system = named('system');
            const hybrid = named('hybrid');
            const manual = named('manual');
            return (
              <div className="sp-legend">
                <div className="sp-legend-row">
                  <span className="sp-legend-chip g-manual" />
                  <div>
                    <strong>{t('xMaster.legendManualHead', { n: manual.length })}</strong>{' '}
                    {t('xMaster.legendManualBody')}
                    <code>{manual.join(', ')}</code>
                  </div>
                </div>
                <div className="sp-legend-row">
                  <span className="sp-legend-chip g-hybrid" />
                  <div>
                    <strong>{t('xMaster.legendHybridHead', { n: hybrid.length })}</strong>{' '}
                    {t('xMaster.legendHybridBody')} <code>{hybrid.join(', ')}</code>
                  </div>
                </div>
                <div className="sp-legend-row">
                  <span className="sp-legend-chip g-system" />
                  <div>
                    <strong>{t('xMaster.legendSystemHead', { n: system.length })}</strong>{' '}
                    {t('xMaster.legendSystemBody')} <code>{system.join(', ')}</code>
                  </div>
                </div>
              </div>
            );
          })()}

          {masterForm && (() => {
            const viewing = masterForm.mode === 'view';
            const editable = new Set(viewing ? [] : masterFormEditable(masterForm));
            const anyFilled = [...editable].some((k) => String(masterForm.values[k] ?? '').trim() !== '');
            return (
              <div className="sp-newrow">
                <h4>
                  {masterForm.mode === 'new'
                    ? t('xMaster.newRow')
                    : (viewing ? t('xMaster.viewRow', { key: masterForm.row.Row_Key })
                      : t('xMaster.editRow', { key: masterForm.row.Row_Key }))}
                  {viewing && <span className="opa-ro-tag" style={{ marginLeft: 10 }}><Eye size={12} /> {t('xMaster.viewOnly')}</span>}
                </h4>
                <p className="sp-sub">
                  {viewing ? t('xMaster.subView')
                    : masterForm.mode === 'new'
                    ? <Trans t={t} i18nKey="xMaster.subNew" components={{ c: <code /> }} />
                    : (masterForm.row.Is_Manual
                      ? t('xMaster.subManual')
                      : <Trans t={t} i18nKey="xMaster.subLedger" components={{ b: <strong /> }} />)}
                </p>
                <div className="sp-newrow-grid">
                  {MASTER_FORM_FIELDS.map((f) => {
                    const label = master?.labels?.[f.key] || f.key.replace(/_/g, ' ');
                    if (editable.has(f.key)) {
                      const set = (value) => setMasterForm((prev) => ({
                        ...prev, values: { ...prev.values, [f.key]: value },
                      }));
                      // Factory is a strict pick: the sheet is defined as the six plants, and a seventh
                      // spelling is exactly the mess the vendor-name folding exists to clean up.
                      if (f.key === 'Factory') {
                        return (
                          <label key={f.key}>
                            <span>{label}</span>
                            <select
                              className="sp-input"
                              value={masterForm.values[f.key] ?? ''}
                              onChange={(e) => {
                                set(e.target.value);
                                if (masterForm.mode === 'new') suggestVendorPo(e.target.value);
                              }}
                            >
                              <option value="">—</option>
                              {(masterOptions?.Factory || []).map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </label>
                        );
                      }
                      /* A fixed set of picks comes from the API (`choices`), not from a constant here,
                         so the dropdown and the validator that rejects anything else cannot drift. */
                      const choices = master?.choices?.[f.key];
                      if (choices?.length) {
                        return (
                          <label key={f.key}>
                            <span>{label}</span>
                            <select
                              className="sp-input"
                              value={masterForm.values[f.key] ?? ''}
                              onChange={(e) => set(e.target.value)}
                            >
                              <option value="">—</option>
                              {choices.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </label>
                        );
                      }
                      const suggestions = masterOptions?.[f.key];
                      return (
                        <label key={f.key}>
                          <span>{label}</span>
                          <input
                            className="sp-input"
                            type={f.type || 'text'}
                            list={suggestions?.length ? `ms-list-${f.key}` : undefined}
                            value={masterForm.values[f.key] ?? ''}
                            onChange={(e) => set(e.target.value)}
                          />
                          {/* The running number, and where it came from. A number the portal made up
                              should say so before somebody writes it on a real purchase order. */}
                          {f.key === 'Vendor_PO' && masterForm.mode === 'new' && !!masterNextVendorPo && (
                            <em>
                              {masterNextVendorPo.next
                                ? t(masterNextVendorPo.guessed ? 'xMaster.nextAfterGuessed' : 'xMaster.nextAfter',
                                  { po: masterNextVendorPo.highest || '—' })
                                : masterNextVendorPo.note}
                            </em>
                          )}
                          {/* Said where it is being decided, not only on the grid: the box is
                              pre-filled with the effective date, so without this line there is
                              nothing on screen to show a typed value differs from the ledger's. */}
                          {/* The same for the customer PO: the box holds the effective value, so this is
                              the only place that says what the ledger (or the split) has underneath. */}
                          {f.key === 'Customer_PO' && masterForm.mode === 'edit'
                            && !masterForm.row?.Is_Manual && (
                            <em>
                              {masterForm.row?.Customer_PO_System
                                ? t(masterForm.row?.Customer_PO_Manual ? 'xMaster.sysHasCpoTyped' : 'xMaster.sysHas',
                                  { v: masterForm.row.Customer_PO_System })
                                : t('xMaster.sysNoCpo')}
                            </em>
                          )}
                          {f.key === 'Original_Delivery_Date' && masterForm.mode === 'edit'
                            && !masterForm.row?.Is_Manual && (
                            <em>
                              {masterForm.row?.Delivery_Date_System
                                ? t(masterForm.row?.Delivery_Date_Manual ? 'xMaster.sysHasDateTyped' : 'xMaster.sysHas',
                                  { v: masterDateValue(masterForm.row.Delivery_Date_System) })
                                : t('xMaster.sysNoDate')}
                            </em>
                          )}
                          {!!suggestions?.length && (
                            <datalist id={`ms-list-${f.key}`}>
                              {suggestions.map((v) => <option key={v} value={v} />)}
                            </datalist>
                          )}
                        </label>
                      );
                    }
                    // Read-only context. Rendered as text rather than a disabled input: a greyed-out box
                    // still looks like something you failed to type into.
                    return (
                      <label key={f.key} className="sp-ro">
                        <span>{label}</span>
                        <div className="sp-ro-value">
                          {fmtMasterCell(f.key, masterForm.row?.[f.key])}
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="sp-note-actions">
                  {masterForm.mode === 'edit' && masterForm.row?.Is_Manual && (
                    <button
                      type="button"
                      className="sp-btn sp-btn-danger"
                      disabled={masterSaving}
                      onClick={() => deleteMasterRow(masterForm.row)}
                    >
                      {t('xMaster.deleteRow')}
                    </button>
                  )}
                  <button type="button" className="sp-btn" onClick={() => setMasterForm(null)}>{viewing ? t('xMaster.close') : t('xMaster.cancel')}</button>
                  {!viewing && (
                  <button
                    type="button"
                    className="sp-btn primary"
                    // No field is required; there just has to be something to save.
                    disabled={masterSaving || !anyFilled}
                    onClick={saveMasterForm}
                  >
                    {masterSaving ? t('xMaster.saving') : (masterForm.mode === 'new' ? t('xMaster.saveRow') : t('xMaster.saveChanges'))}
                  </button>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="sp-toolbar">
            <input
              className="sp-input"
              placeholder={t('xMaster.searchPh')}
              defaultValue={masterQuery.search}
              onKeyDown={(e) => { if (e.key === 'Enter') setMasterFilter({ search: e.target.value }); }}
              onBlur={(e) => setMasterFilter({ search: e.target.value })}
            />
            <select
              className="sp-input"
              value={masterQuery.source}
              onChange={(e) => setMasterFilter({ source: e.target.value })}
            >
              <option value="">{t('xMaster.srcAll')}</option>
              <option value="SAP">SAP (SO + PO)</option>
              <option value="NS">NetSuite</option>
              <option value="MANUAL">{t('xMaster.srcManual')}</option>
              {/* Only where the server puts planned rows on the sheet (SP_MASTER_PLANNED) — a filter
                  that can only ever return nothing should not be offered. */}
              {master?.planned && <option value="PLANNED">{t('xMaster.srcPlanned')}</option>}
            </select>
            <span className="sp-sub">
              {t('xMaster.rowsOf', { n: masterRows.length.toLocaleString('en-GB'), total: (master?.total ?? 0).toLocaleString('en-GB') })}
            </span>
            {(!!Object.keys(masterQuery.filters).length || !!masterQuery.sort || !!masterQuery.search
              || !!masterQuery.source) && (
              <button
                type="button"
                className="sp-btn"
                onClick={() => setMasterQuery({ source: '', search: '', sort: '', dir: 'desc', filters: {} })}
              >
                {t('xMaster.clearSortFilters')}
              </button>
            )}
          </div>

          {masterError && <div className="sp-alert err">{masterError}</div>}

          {/* The box is sized at 15 rows; the subtotal rows are pinned to its bottom and would sit on
              top of the last two. 31px is the measured height of one rendered subtotal row — the
              28px first guessed from the padding was wrong. */}
          <div
            className="sp-table-wrap sp-scrollmaster"
            ref={masterBoxRef}
            onScroll={onMasterScroll}
            style={{ '--sp-subtotal-h': `${(master?.totals?.length || 0) * 31}px` }}
          >
            <table className="sp-table">
              <thead>
                <SPLedgerHeader
                  leading={<th className="sp-rownum">{t('xMaster.thNo')}</th>}
                  trailing={<th className="sp-actioncell">{t('xMaster.thAction')}</th>}
                  columns={visibleMasterColumns}
                  labels={master?.labels || {}}
                  // Which columns a person has to type. Comes from the API so the screen and the
                  // server cannot disagree about whose job an empty cell is.
                  groups={master?.groups || {}}
                  sort={{ key: masterQuery.sort, dir: masterQuery.dir }}
                  filters={masterQuery.filters}
                  apiBase={API_BASE}
                  valuesPath="master-values"
                  valueParams={{ search: masterQuery.search, source: masterQuery.source }}
                  query={masterQuery}
                  onSort={(key, dir) => setMasterFilter({
                    sort: key,
                    dir: dir || (masterQuery.sort === key && masterQuery.dir === 'asc' ? 'desc' : 'asc'),
                  })}
                  onFilter={(key, picked) => setMasterFilter({
                    filters: (() => {
                      const next = { ...masterQuery.filters };
                      if (picked.length) next[key] = picked; else delete next[key];
                      return next;
                    })(),
                  })}
                />
              </thead>
              <tbody>
                {masterRows.map((row, index) => (
                  /* data-rowkey so a row can be addressed by WHAT IT IS rather than by where it
                     sits: this table is filtered, sorted and windowed, so an index is a different
                     row on the next render. The same reason .pad-pick exists on the pricing cards. */
                  <tr key={row.Row_Key} data-rowkey={row.Row_Key}
                      className={row.Is_Manual ? 'sp-row-manual' : (row.Source === 'PLANNED' ? 'sp-row-planned' : '')}>
                    <td className="sp-rownum">{index + 1}</td>
                    {visibleMasterColumns.map((column) => {
                      const owned = (master?.editable || []).includes(column);
                      const filled = row[column] !== null && row[column] !== undefined && row[column] !== '';
                      const locked = (column === 'Cycle_No' && row.Cycle_No_Locked)
                        || (column === 'Customer_PO' && row.Customer_PO_Locked);
                      /* ORIGINAL DELIVERY DATE CANNOT USE THE "editable AND filled" TEST. It became
                         editable on 2026-10-02 and nearly every row has a date, so that rule would
                         have lit the whole column and the highlight would have stopped meaning
                         anything. Its own flag says whether THIS one was typed. */
                      const dateCol = column === 'Original_Delivery_Date';
                      /* THE CUSTOMER PO TOO (2026-10-20): editable on every row now, so its own flag says
                         whether THIS one was typed over the ledger's. */
                      const cpoCol = column === 'Customer_PO';
                      const typed = dateCol
                        ? !!row.Delivery_Date_Manual
                        : cpoCol ? !!row.Customer_PO_Manual && !row.Is_Manual
                          : (owned && filled && !locked);
                      /* What the ledgers answered, kept beside what a person typed over it. A
                         correction that erases the thing it corrected cannot be told from a
                         mistake. */
                      const systemNote = (dateCol && typed)
                        ? (row.Delivery_Date_System
                          ? t('xMaster.typedSysHas', { v: String(row.Delivery_Date_System).slice(0, 10) })
                          : t('xMaster.typedNoDate'))
                        : (cpoCol && typed)
                          ? (row.Customer_PO_System
                            ? t('xMaster.typedSysHas', { v: row.Customer_PO_System })
                            : t('xMaster.typedNoCpo'))
                          : undefined;
                      const cls = [
                        // Same three groups as the header, so a column reads as one block top to bottom.
                        `g-${master?.groups?.[column] || 'system'}`,
                        column === 'Remark' ? 'sp-col-remark' : '',
                        master?.unsourced?.includes(column) && !filled ? 'sp-unsourced' : '',
                        MASTER_MONEY.has(column) || MASTER_NUM.has(column) || MASTER_PLAIN_NUM.has(column) ? 'num' : '',
                        // What a person typed is marked, so an edit stays visible after the save.
                        typed ? 'sp-typed' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <td
                          key={column}
                          className={cls}
                          title={locked ? t('xMaster.lockedHint') : systemNote}
                        >
                          {/* A PLANNED ROW HAS NO PO, and the cell says so rather than standing empty:
                              blank reads as "not filled in yet", which is a different fact. Its own
                              hover carries where the row and its reference price came from. */}
                          {row.Source === 'PLANNED' && column === 'HNX_PO'
                            ? <span className="sp-planned-chip" title={row.Price_Source || ''}>{t('xMaster.plannedChip')}</span>
                            : fmtMasterCell(column, row[column])}
                          {/* A MARK, not only a background. The highlight answers "which ones are
                              typed" at a glance and nothing else; this says so in the cell, carries
                              the system's own figure on its own hover, and survives a printout or a
                              forced-colour mode where the tint is gone. */}
                          {!!systemNote && <sup className="sp-typed-mark" title={systemNote}>✎</sup>}
                        </td>
                      );
                    })}
                    {/* The action column lives at the END and stays frozen to the right edge (Max,
                        2026-08-28), so the buttons are reachable without scrolling back across 32 columns.
                        Delete appears only on a typed row — a ledger row cannot be deleted, and a button
                        that is always refused should not be offered. */}
                    <td className="sp-actioncell">
                      <button type="button" className="sp-mini-btn" onClick={() => openMasterEdit(row, true)} title={t('xMaster.viewHint')}>
                        <Eye size={12} /> {t('xMaster.view')}
                      </button>
                      {/* A PLANNED ROW IS READ, NEVER EDITED HERE (Max, 2026-10-12, sql/20261012g): it is
                          the Capacity line itself, so the workbook is where it changes — and the server
                          refuses an edit with that sentence. Split has no ledger line to split. */}
                      {row.Source === 'PLANNED' ? (
                        <span className="sp-planned-note" title={t('xMaster.plannedHint')}>
                          {t('xMaster.fromCapacity')}
                        </span>
                      ) : (
                      <button type="button" className="sp-mini-btn" onClick={() => openMasterEdit(row)}>
                        <Pencil size={12} /> {t('xMaster.edit')}
                      </button>
                      )}
                      {/* Any ledger line with a quantity can be split — PO, SO or NetSuite. It was
                          PO-only at first and Max found the missing buttons straight away (2026-08-28):
                          an SO row carrying one destination is exactly the row somebody needs to split
                          when the shipment turns out to go two places. A hand-typed row is excluded
                          because there is nothing to add up to — just add another row. */}
                      {row.Source !== 'PLANNED' && (row.Is_Split || (!row.Is_Manual && Number(row.Quantity) > 0)) && (
                        <button
                          type="button"
                          className={`sp-mini-btn ${row.Is_Split ? 'sp-mini-on' : ''}`}
                          onClick={() => openMasterSplit(row)}
                          title={row.Is_Split ? t('xMaster.splitEditHint') : t('xMaster.splitTitle')}
                        >
                          <Split size={12} /> {t('xMaster.split')}
                        </button>
                      )}
                      {row.Is_Manual && (
                        <button
                          type="button"
                          className="sp-mini-btn sp-mini-danger"
                          onClick={() => setMasterConfirmDel(row)}
                        >
                          <Trash2 size={12} /> {t('xMaster.del')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!masterRows.length && !masterLoading && (
                  <tr>
                    <td className="sp-empty" colSpan={(master?.columns?.length || 32) + 2}>{t('xMaster.noRows')}</td>
                  </tr>
                )}
              </tbody>
              {/* SUBTOTALS — Tram via Max, 2026-10-01, marking Quantity and the two Total amount
                  columns. The figures come from the SERVER over the whole filtered set: this table
                  shows 50 rows of 26,000 at a time, so adding up what is loaded would print a total
                  under a column header that covers only the pages somebody scrolled past.
                  ONE ROW PER CURRENCY, which is why this is a list and not a single line. The sheet
                  holds USD, KRW and rows carrying none, a filter is not always one PO, and two
                  currencies added together are a number with no unit.
                  Each cell goes through fmtMasterCell with its OWN column, so a subtotal is
                  formatted exactly like the figures above it rather than by a second rule. */}
              {!!master?.totals?.length && (
                <tfoot className="sp-subtotals">
                  {master.totals.map((sum) => (
                    <tr key={sum.currency || '(none)'}>
                      <td className="sp-rownum" />
                      {visibleMasterColumns.map((column, index) => {
                        const totalled = (master?.totalColumns || []).includes(column);
                        /* The label sits in the first visible column, which is always Factory:
                           MASTER_COL_LOCKED keeps it on screen and it leads SP_MASTER_COLUMNS, so
                           this cell can never be one that carries a figure. */
                        if (index === 0) {
                          return (
                            <td key={column} className="sp-subtotal-label">
                              {sum.currency ? t('xMaster.subtotal', { cur: sum.currency }) : t('xMaster.subtotalNoCur')}
                              <span className="sp-subtotal-rows">
                                {' '}{t('xMaster.subtotalRows', { n: Number(sum.rows_).toLocaleString('en-GB') })}
                              </span>
                            </td>
                          );
                        }
                        return (
                          <td key={column} className={totalled ? 'num' : ''}>
                            {totalled ? fmtMasterCell(column, sum[column]) : ''}
                          </td>
                        );
                      })}
                      <td className="sp-actioncell" />
                    </tr>
                  ))}
                </tfoot>
              )}
            </table>
          </div>

          {masterLoading && !!masterRows.length && <p className="sp-note">{t('xMaster.loadingMore')}</p>}

          {/* THE DESTINATION MAPPING, THE SAME COMPONENT SAP SHIP DOC DRAWS — Max, 2026-10-03.
              `api` is the only prop that differs: that screen offers the 46 destinations its own
              document has held, this offers the 116 the sheet holds, and both write one table, so a
              name settled on either is settled on both. Two copies of this screen is how one table
              comes to be edited two ways.
              It reloads the sheet on save, because the column now PRINTS the mapped name — without
              that the panel would say the name is stored while the table behind it still shows the
              Korean, which is the fault the Ship Doc check caught when this was written. */}
          {destOpen && (
            <div className="sp-modal-back" onClick={() => setDestOpen(false)}>
              <div className="sp-modal sp-destbox" onClick={(e) => e.stopPropagation()}>
                <div className="sp-modal-h">
                  <div>
                    <strong>{t('xMaster.destNames')}</strong>
                    <small>{t('xMaster.destShared')}</small>
                  </div>
                  <button type="button" className="sp-icon-btn" aria-label={t('xMaster.close')}
                    onClick={() => setDestOpen(false)}><X size={15} /></button>
                </div>
                <div className="sp-modal-body">
                  <DestinationStep
                    canEdit={mayEditMaster}
                    api={`${API_BASE}/master-sheet`}
                    scopeLabel={t('xMaster.destScope')}
                    onApplied={() => loadMaster(masterQuery, { append: false })}
                  />
                </div>
              </div>
            </div>
          )}

          {changesOpen && (
            <MasterSheetChanges api={API_BASE} fmt={fmtMasterCell} onClose={() => setChangesOpen(false)} />
          )}

          {masterConfirmDel && (
            <div className="sp-modal-back" onClick={() => setMasterConfirmDel(null)}>
              <div className="sp-modal sp-askbox" onClick={(e) => e.stopPropagation()}>
                <div className="sp-modal-h">
                  <div>
                    <strong>{t('xMaster.delAsk')}</strong>
                    <small>{masterConfirmDel.Row_Key}</small>
                  </div>
                </div>
                <div className="sp-modal-body">
                  <p className="sp-note">
                    {t('xMaster.delBody')}
                  </p>
                </div>
                <div className="sp-modal-f">
                  <button type="button" className="sp-btn" onClick={() => setMasterConfirmDel(null)}>
                    {t('xMaster.cancel')}
                  </button>
                  <button
                    type="button"
                    className="sp-btn sp-btn-danger"
                    onClick={() => deleteMasterRow(masterConfirmDel)}
                  >
                    <Trash2 size={13} /> {t('xMaster.delConfirm')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {masterSplit && (
            <div className="sp-modal-back" onClick={() => !masterSplit.saving && setMasterSplit(null)}>
              <div className="sp-modal sp-splitbox" onClick={(e) => e.stopPropagation()}>
                <div className="sp-modal-h">
                  <div>
                    <strong>{t('xMaster.splitTitle')}</strong>
                    <small>
                      {masterSplit.parent
                        ? `${masterSplit.parent.hnxPo} · ${masterSplit.parent.code}`
                          + `${masterSplit.parent.description ? ` · ${masterSplit.parent.description}` : ''}`
                        : masterSplit.parentKey}
                    </small>
                  </div>
                  <button type="button" className="sp-icon-btn" aria-label={t('xMaster.close')} onClick={() => setMasterSplit(null)}>
                    <X size={16} />
                  </button>
                </div>

                {/* One wrapper with the padding on it, rather than padding every child through a
                    `.sp-modal > *` selector. That selector lost to `.sp-splitsum { padding: … }`,
                    which is declared later — shorthand padding replaces the longhand outright — and
                    the sum bar ended up against the edge (Max, twice). A wrapper cannot be
                    overridden by a child's own padding. */}
                <div className="sp-modal-body">
                {masterSplit.loading && <p className="sp-note">{t('xMaster.loading')}</p>}
                {!!masterSplit.error && <div className="sp-alert err">{masterSplit.error}</div>}

                {!masterSplit.loading && masterSplit.parent && (
                  <>
                    {/* The sum rule, live. Somebody typing into six lines should never have to open a
                        calculator to find out why Save is refusing them. */}
                    <div className={`sp-splitsum ${splitRemainder === 0 ? 'ok' : 'off'}`}>
                      <span>{t('xMaster.lineQty')} <b>{splitParentQty.toLocaleString('en-GB')}</b></span>
                      <span>{t('xMaster.allocated')} <b>{splitTotal.toLocaleString('en-GB')}</b></span>
                      <span>
                        {splitRemainder === 0 ? t('xMaster.balanced') : (splitRemainder > 0
                          ? <>{t('xMaster.leftToAllocate')} <b>{splitRemainder.toLocaleString('en-GB')}</b></>
                          : <>{t('xMaster.overBy')} <b>{Math.abs(splitRemainder).toLocaleString('en-GB')}</b></>)}
                      </span>
                    </div>

                    <table className="sp-splittable">
                      <colgroup>
                        <col className="c-num" /><col className="c-cpo" /><col />
                        <col className="c-qty" /><col className="c-chk" /><col className="c-del" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>#</th><th>Customer PO#</th><th>{t('xMaster.thDestination')}</th>
                          <th className="num">{t('xMaster.thQuantity')}</th><th>{t('xMaster.thStock')}</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {masterSplit.children.map((c, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={i} className={c.isStock ? 'is-stock' : ''}>
                            <td className="sp-rownum">{i + 1}</td>
                            <td>
                              <input
                                className="sp-input"
                                value={c.customerPo}
                                disabled={c.isStock}
                                onChange={(e) => setSplitChildren((rows) => rows.map((r, j) => (
                                  j === i ? { ...r, customerPo: e.target.value } : r)))}
                              />
                            </td>
                            <td>
                              {/* A list, not a free-text box: 53 destinations already exist in the
                                  ledgers and a 54th spelling of one of them is the mess the vendor
                                  name folding exists to clean up. `list` rather than <select> so a
                                  genuinely new destination can still be typed. */}
                              <input
                                className="sp-input"
                                list="ms-split-dest"
                                value={c.destination}
                                disabled={c.isStock}
                                onChange={(e) => setSplitChildren((rows) => rows.map((r, j) => (
                                  j === i ? { ...r, destination: e.target.value } : r)))}
                              />
                            </td>
                            <td className="num">
                              <input
                                className="sp-input num"
                                type="number"
                                value={c.qty}
                                onChange={(e) => setSplitChildren((rows) => rows.map((r, j) => (
                                  j === i ? { ...r, qty: e.target.value } : r)))}
                              />
                            </td>
                            <td className="mid">
                              <input
                                type="checkbox"
                                checked={c.isStock}
                                title={t('xMaster.stockHint')}
                                onChange={(e) => setSplitChildren((rows) => rows.map((r, j) => (
                                  j === i
                                    ? { ...r, isStock: e.target.checked,
                                        customerPo: e.target.checked ? '' : r.customerPo,
                                        destination: e.target.checked ? '' : r.destination }
                                    : r)))}
                              />
                            </td>
                            <td className="mid">
                              <button
                                type="button"
                                className="sp-mini-btn sp-mini-danger"
                                onClick={() => setSplitChildren((rows) => rows.filter((r, j) => j !== i))}
                              >
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!masterSplit.children.length && (
                          <tr><td className="sp-empty" colSpan={6}>{t('xMaster.notSplit')}</td></tr>
                        )}
                      </tbody>
                    </table>

                    <div className="sp-splitbtns">
                      <button
                        type="button"
                        className="sp-btn"
                        onClick={() => setSplitChildren((rows) => [...rows,
                          { customerPo: '', destination: '',
                            qty: splitRemainder > 0 ? String(splitRemainder) : '', isStock: false, note: '' }])}
                      >
                        <Plus size={14} /> {t('xMaster.addLine')}
                      </button>
                      {splitRemainder > 0 && (
                        <button
                          type="button"
                          className="sp-btn"
                          onClick={() => setSplitChildren((rows) => [...rows,
                            { customerPo: '', destination: '', qty: String(splitRemainder),
                              isStock: true, note: '' }])}
                        >
                          {t('xMaster.putOnStock', { n: splitRemainder.toLocaleString('en-GB') })}
                        </button>
                      )}
                    </div>

                    <datalist id="ms-split-dest">
                      {(masterOptions?.Destination || []).map((v) => <option key={v} value={v} />)}
                    </datalist>
                  </>
                )}
                </div>

                {!masterSplit.loading && masterSplit.parent && (
                  <div className="sp-modal-f">
                      {!!masterSplit.children.length && splitRemainder !== 0 && (
                        <span className="sp-splitwarn">
                          {t('xMaster.mustAddUp', { n: splitParentQty.toLocaleString('en-GB') })}
                        </span>
                      )}
                      <button type="button" className="sp-btn" onClick={() => setMasterSplit(null)}>
                        {t('xMaster.cancel')}
                      </button>
                      <button
                        type="button"
                        className="sp-btn primary"
                        disabled={!!masterSplit.saving
                          || (!!masterSplit.children.length && splitRemainder !== 0)}
                        onClick={saveMasterSplit}
                      >
                    {masterSplit.saving
                      ? t('xMaster.saving')
                      : (masterSplit.children.length
                        ? t('xMaster.saveLines', { n: masterSplit.children.length })
                        : t('xMaster.removeSplit'))}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
        </div>
      </div>
    </div>
  );
}
